import { useState, useEffect, useMemo } from "react";
import { Check, ChevronsUpDown, Search, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export function UserSearchSelect({ 
  users = [], 
  employees = [], 
  currentUser = null,
  value = "",
  onChange,
  placeholder = "Select user...",
  allowUnassigned = false,
  disabled = false
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Combine users and employees, using email as unique identifier
  const combinedUsers = useMemo(() => {
    const combined = [];
    const seenEmails = new Set();
    
    // Add current user first with "(Me)" label
    if (currentUser) {
      combined.push({
        id: currentUser.id,
        name: currentUser.name,
        email: currentUser.email,
        label: `${currentUser.name} (Me)`,
        type: 'user',
        role: currentUser.role
      });
      seenEmails.add(currentUser.email.toLowerCase());
    }
    
    // Add other users
    users.forEach(u => {
      if (!seenEmails.has(u.email.toLowerCase())) {
        combined.push({
          id: u.id,
          name: u.name,
          email: u.email,
          label: `${u.name} (${u.role || 'User'})`,
          type: 'user',
          role: u.role
        });
        seenEmails.add(u.email.toLowerCase());
      }
    });
    
    // Add employees who don't have user accounts
    employees.forEach(emp => {
      if (!seenEmails.has(emp.email.toLowerCase())) {
        combined.push({
          id: emp.id,
          name: emp.name,
          email: emp.email,
          label: `${emp.name} (${emp.designation || 'Employee'})`,
          type: 'employee',
          designation: emp.designation
        });
        seenEmails.add(emp.email.toLowerCase());
      }
    });
    
    return combined;
  }, [users, employees, currentUser]);

  // Filter based on search
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return combinedUsers;
    const term = searchTerm.toLowerCase();
    return combinedUsers.filter(u => 
      u.name.toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term) ||
      (u.role && u.role.toLowerCase().includes(term)) ||
      (u.designation && u.designation.toLowerCase().includes(term))
    );
  }, [combinedUsers, searchTerm]);

  const selectedUser = combinedUsers.find(u => u.id === value);

  const handleSelect = (userId) => {
    if (userId === "unassigned") {
      onChange("", "");
    } else {
      const user = combinedUsers.find(u => u.id === userId);
      if (user) {
        onChange(user.id, user.name);
      }
    }
    setOpen(false);
    setSearchTerm("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between rounded-lg font-normal"
          disabled={disabled}
          data-testid="user-search-select"
        >
          {selectedUser ? (
            <div className="flex items-center gap-2 truncate">
              <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <span className="truncate">{selectedUser.label}</span>
            </div>
          ) : (
            <span className="text-slate-500">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[350px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Search by name or email..." 
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList>
            <CommandEmpty>No user found.</CommandEmpty>
            <CommandGroup>
              {allowUnassigned && (
                <CommandItem
                  value="unassigned"
                  onSelect={() => handleSelect("unassigned")}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      !value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="text-slate-500">Unassigned</span>
                </CommandItem>
              )}
              {filteredUsers.map((user) => (
                <CommandItem
                  key={user.id}
                  value={user.id}
                  onSelect={() => handleSelect(user.id)}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === user.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">{user.label}</span>
                    <span className="text-xs text-slate-500">{user.email}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
